import {
  DialogContent,
  Grid,
  IconButton,
  Paper,
  TextField,
  Toolbar,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import PrintIcon from '@material-ui/icons/Print';
import RemoveIcon from '@material-ui/icons/RemoveCircle';
import { Autocomplete } from '@material-ui/lab';
import { ResponsiveIconButton } from 'components';
import { DialogControlProps, DialogControls } from 'components/DialogControls';
import { MenuItem, SetMenu } from 'interfaces';
import * as React from 'react';
import { ChooseSetMenuDialog, SetMenuItemForm } from './ChooseSetMenuDialog';
import { OrderForm, OrderItemsForm } from './SaveOrderDialog';
import { useReactToPrint } from 'react-to-print';
import { PrintOrderReceipt } from 'components';
import { useRef } from 'react';
import { SetMenuService } from 'services';
import { TabletKeyboard } from './TabletKeyboard';

interface Props extends DialogControlProps {
  menuItems: MenuItem[];
  readonly?: boolean;
  orderId?: number;
  form: OrderForm;
  setForm: React.Dispatch<React.SetStateAction<OrderForm>>;
}

const useStyles = makeStyles((theme) => ({
  toolbar: {
    justifyContent: 'space-between',
    minHeight: 0,
    marginTop: theme.spacing(1),
    paddingRight: 0,
    paddingLeft: 0,
  },
  removeIcon: {
    color: theme.palette.danger.main,
  },
  table: {
    marginBottom: theme.spacing(5),
  },
  pickerWrap: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    background: '#fafafa',
    borderLeft: '4px solid ' + theme.palette.primary.main,
  },
  itemCard: {
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1),
  },
  itemTitle: {
    fontWeight: 600,
    lineHeight: 1.2,
  },
  setMenuTag: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  emptyCart: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    background: '#fafafa',
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
  // Two-column layout: left = picker/actions, right = cart preview.
  leftCol: {
    [theme.breakpoints.up('md')]: {
      borderRight: '1px solid ' + theme.palette.divider,
      paddingRight: theme.spacing(2),
    },
  },
  rightCol: {
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(2),
    },
  },
  cartScroll: {
    // Cap at roughly 4 cards on a full-height viewport. On tablets when
    // the soft keyboard is open the viewport shrinks to ~half, so we cap
    // at 35vh as well — the picker stays visible above the keyboard and
    // the cart scrolls inside the remaining space.
    maxHeight: 'min(420px, 35vh)',
    overflowY: 'auto',
    paddingRight: 4,
  },
  // Compact spacing when the viewport is short (tablet + soft keyboard
  // open). Tightens the picker, cards, and toolbar so the layout stays
  // legible inside the half-viewport the OS keyboard leaves us.
  '@media (max-height: 700px)': {
    pickerWrap: {
      padding: theme.spacing(0.5, 1.5),
      marginBottom: theme.spacing(1),
    },
    setMenuCard: {
      padding: theme.spacing(0.5, 1),
      marginBottom: theme.spacing(0.5),
    },
    itemCard: {
      padding: theme.spacing(0.5, 1),
      marginBottom: theme.spacing(0.5),
    },
    toolbar: {
      marginTop: 0,
    },
  },
  // Bigger tap targets in the search results dropdown — finger-friendly
  // on a tablet. Each result row is ~56px tall, matches the Material
  // touch-target guideline.
  pickerOption: {
    padding: theme.spacing(1, 1.5),
    minHeight: 56,
    fontSize: '1rem',
    lineHeight: 1.3,
  },
  // ----- Tablet picker styles -----
  // Search input shown above the on-screen keyboard. readOnly + a
  // custom font size to make it scan-able from arm's length.
  tabletSearch: {
    marginBottom: theme.spacing(1),
    '& input': {
      fontSize: '1.4rem',
      padding: theme.spacing(1.5),
      // Prevent iOS/Android from showing the OS keyboard even on tap.
      caretColor: theme.palette.primary.main,
    },
  },
  // The middle suggestions column. Its own column between search and
  // preview, so matches never render on top of the search field or
  // keyboard — the previous floating dropdown overlapped the field and
  // clipped most of the suggestions on a tablet (2026-07-21).
  middleCol: {
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  tabletResults: {
    minHeight: 200,
    maxHeight: 'min(70vh, 640px)',
    overflowY: 'auto',
    border: '1px solid ' + theme.palette.divider,
    borderRadius: 4,
    background: '#fff',
  },
  // Muted centred hint shown when there is nothing to list, so the empty
  // column does not read as a broken blank box.
  tabletResultsEmpty: {
    minHeight: 180,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
    textAlign: 'center',
    padding: theme.spacing(0, 2),
  },
  tabletResultRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: 56,
    padding: theme.spacing(1, 1.5),
    border: 'none',
    borderBottom: '1px solid ' + theme.palette.divider,
    background: '#fff',
    fontSize: '1.05rem',
    fontWeight: 500,
    textAlign: 'left',
    cursor: 'pointer',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    '&:active': { background: '#e3f2fd' },
    '&:last-child': { borderBottom: 'none' },
  },
  tabletResultKind: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginLeft: theme.spacing(1),
  },
  keyboardWrap: {
    // No top margin — the preceding element (search bar OR results
    // panel) carries its own marginBottom, so the keyboard sits with a
    // single 8px gap from whatever is above it.
    marginTop: 0,
  },
  // Set-menu group card: parent line + indented choices.
  setMenuCard: {
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1),
    background: '#f5f5f5',
    borderLeft: '4px solid ' + theme.palette.primary.main,
  },
  setMenuHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  setMenuName: {
    fontWeight: 700,
  },
  setMenuPrice: {
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
  },
  setMenuChoices: {
    paddingLeft: theme.spacing(2),
    borderLeft: '2px solid ' + theme.palette.divider,
    marginLeft: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  setMenuChoice: {
    fontSize: '0.88rem',
    lineHeight: 1.5,
  },
  setMenuNote: {
    marginTop: theme.spacing(1),
    background: '#fff',
  },
  totalLine: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1),
    borderTop: '1px solid ' + theme.palette.divider,
  },
}));

export const OrderStep: React.FC<Props> = ({
  menuItems,
  readonly,
  orderId,
  form,
  setForm,
  ...dialogControlProps
}) => {
  const classes = useStyles();

  const [openSetMenu, setOpenSetMenu] = React.useState(false);
  // When the user picks a set menu from the top search, we open the
  // choices dialog with that set menu preselected. Null = the "Add Set
  // Menu" toolbar flow (kept hidden today but the dialog still supports
  // it for legacy code paths).
  const [preselectedSetMenuId, setPreselectedSetMenuId] = React.useState<
    number | undefined
  >(undefined);
  const componentRef = useRef(null);
  const [maxSetMenuItemGrouped, setMaxSetMenuItemGrouped] = React.useState(0);
  // The top "Add item" autocomplete is fully controlled — we hold its
  // input text in state and clear it after every pick. We deliberately
  // do NOT remount on each pick (the old `pickerKey++` approach) because
  // on tablet a remount causes the soft keyboard to flicker closed and
  // re-open between every item. Same instance + refocus = no flicker,
  // keyboard stays up, staff can chain items without re-tapping.
  const [pickerInput, setPickerInput] = React.useState('');
  const pickerInputRef = useRef<HTMLInputElement | null>(null);

  // Tablet mode: replace the OS soft keyboard (which fights the dialog
  // for vertical space and flickers between item adds) with our own
  // on-screen QWERTY rendered inside the dialog. Detection runs once on
  // mount; staff machines are either a real PC (mouse, no touch) or a
  // tablet (touch). Hybrid laptops with a touchscreen + keyboard would
  // see the on-screen keyboard but their physical keyboard still works
  // for fields outside this dialog. If detection ever gets it wrong we
  // can add a manual toggle.
  const isTouch = React.useMemo(
    () =>
      typeof window !== 'undefined' &&
      ('ontouchstart' in window ||
        (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1)),
    [],
  );

  // All set menus available to add via the unified picker. Fetched once
  // on mount; the picker merges them with menu items into a single
  // searchable list (grouped under a "Set menus" header in the dropdown).
  const [setMenus, setSetMenus] = React.useState<SetMenu[]>([]);
  React.useEffect(() => {
    let cancelled = false;
    SetMenuService.get()
      .then((rows) => {
        if (!cancelled) setSetMenus(rows);
      })
      .catch(() => {
        if (!cancelled) setSetMenus([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Client-side filter for the tablet picker (the desktop Autocomplete
  // does its own filtering internally). Cap at 50 results so the list
  // doesn't blow past the dialog if the user clears their search.
  type PickerOption =
    | { kind: 'item'; id: number; label: string; menuItem: MenuItem }
    | { kind: 'setMenu'; id: number; label: string; setMenu: SetMenu };
  const filteredOptions = React.useMemo<PickerOption[]>(() => {
    const q = pickerInput.trim().toLowerCase();
    if (q === '') return [];
    const items: PickerOption[] = menuItems
      .filter((mi) => mi.title.toLowerCase().includes(q))
      .map((mi) => ({
        kind: 'item' as const,
        id: mi.id,
        label: mi.title,
        menuItem: mi,
      }));
    const sms: PickerOption[] = setMenus
      .filter((sm) => sm.name.toLowerCase().includes(q))
      .map((sm) => ({
        kind: 'setMenu' as const,
        id: sm.id,
        label: sm.name + ' — €' + Number(sm.price).toFixed(2),
        setMenu: sm,
      }));
    // Set menus first — staff usually search for those by name and
    // there are far fewer of them than individual items.
    return [...sms, ...items].slice(0, 50);
  }, [pickerInput, menuItems, setMenus]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  // Group form.items into render units: regular items stay as singletons,
  // set-menu picks (those carrying setMenuGroupId) collapse into one
  // group per set menu so the cart renders them as a parent card with
  // indented choices instead of N flat "Part of …" lines.
  type Unit =
    | { kind: 'item'; index: number; item: OrderItemsForm }
    | {
        kind: 'setMenu';
        groupId: number;
        name: string;
        price: number;
        indices: number[];
        items: OrderItemsForm[];
      };
  const units: Unit[] = [];
  form.items.forEach((it, idx) => {
    const gid = it.setMenuGroupId;
    if (gid) {
      let g = units.find(
        (u) => u.kind === 'setMenu' && u.groupId === gid,
      ) as Extract<Unit, { kind: 'setMenu' }> | undefined;
      if (!g) {
        g = {
          kind: 'setMenu',
          groupId: gid,
          name: it.setMenuName || 'Set menu',
          price: parseFloat(it.setMenuGroupPrice?.toString() ?? '0'),
          indices: [],
          items: [],
        };
        units.push(g);
      }
      g.items.push(it);
      g.indices.push(idx);
    } else {
      units.push({ kind: 'item', index: idx, item: it });
    }
  });

  const totalAmount = readonly
    ? form.items.reduce<number>(
        (prev, curr) =>
          prev + parseFloat(curr.itemCostIncludingVat?.toString() ?? '0'),
        form.setMenuPrice,
      )
    : form.items.reduce<number>(
        (prev, curr) =>
          prev +
          parseFloat(curr.price?.toString() ?? '0') *
            parseFloat(curr.quantity?.toString() ?? '0'),
        parseFloat(form.setMenuPrice.toString() ?? '0'),
      );

  // On a tablet the order picker splits into three columns: search +
  // keyboard on the left, a dedicated suggestions column in the middle,
  // and the cart preview on the right. Off-tablet (or read-only) it stays
  // the original two-column layout.
  const showTabletPicker = !readonly && isTouch;

  return (
    <React.Fragment>
      <DialogContent>
        <Grid container spacing={2}>
          {/* LEFT: search + keyboard + actions */}
          <Grid
            item
            xs={12}
            md={showTabletPicker ? 4 : 5}
            className={classes.leftCol}
          >
            <Toolbar className={classes.toolbar}>
              <ResponsiveIconButton
                onClick={printOrder}
                color="primary"
                icon={PrintIcon}
                disabled={!readonly}
              >
                Print Order
              </ResponsiveIconButton>
            </Toolbar>

            {!readonly && isTouch && (
              <Paper className={classes.pickerWrap} elevation={0}>
                <TextField
                  value={pickerInput}
                  variant="outlined"
                  fullWidth
                  placeholder="Type to search items…"
                  className={classes.tabletSearch}
                  inputProps={{
                    readOnly: true,
                    // inputMode=none tells the browser not to surface
                    // its own keyboard when this field is focused.
                    // Combined with readOnly, the OS keyboard stays
                    // down on iPadOS Safari and Android Chrome.
                    inputMode: 'none',
                  }}
                />
                {/* Suggestions render in their OWN middle column (below),
                    never over the search field or keyboard. */}
                <div className={classes.keyboardWrap}>
                  <TabletKeyboard
                    onKey={(k) => setPickerInput((p) => p + k)}
                    onBackspace={() =>
                      setPickerInput((p) => p.slice(0, -1))
                    }
                    onClear={() => setPickerInput('')}
                    onEnter={() => {
                      const top = filteredOptions[0];
                      if (!top) return;
                      if (top.kind === 'item') handlePickItem(top.menuItem);
                      else handlePickSetMenu(top.setMenu);
                    }}
                    enterDisabled={filteredOptions.length === 0}
                  />
                </div>
              </Paper>
            )}

            {!readonly && !isTouch && (
              <Paper className={classes.pickerWrap} elevation={0}>
                <Autocomplete
                  // Selection state stays null — picking an option fires
                  // onChange + clears the input. We never want the
                  // Autocomplete to display the previously-picked label.
                  value={null}
                  inputValue={pickerInput}
                  onInputChange={(_e, v, reason) => {
                    // 'reset' fires when MUI tries to sync the input to
                    // the selected value (which is null here) — ignoring
                    // it prevents the input from blanking unexpectedly
                    // mid-type. We handle clearing explicitly in
                    // handlePickItem / handlePickSetMenu.
                    if (reason === 'reset') return;
                    setPickerInput(v);
                  }}
                  options={[
                    ...menuItems.map((mi) => ({
                      kind: 'item' as const,
                      id: mi.id,
                      label: mi.title,
                      menuItem: mi,
                    })),
                    ...setMenus.map((sm) => ({
                      kind: 'setMenu' as const,
                      id: sm.id,
                      label: sm.name + ' — €' + Number(sm.price).toFixed(2),
                      setMenu: sm,
                    })),
                  ]}
                  groupBy={(o: any) =>
                    o.kind === 'setMenu' ? 'Set menus' : 'Menu items'
                  }
                  getOptionLabel={(o: any) => (o ? o.label : '')}
                  onChange={(_e: any, picked: any) => {
                    if (!picked) return;
                    if (picked.kind === 'item') {
                      handlePickItem(picked.menuItem);
                    } else {
                      handlePickSetMenu(picked.setMenu);
                    }
                  }}
                  // Enter on the focused result fires onChange just like
                  // a click — that's what makes the type→enter→type loop
                  // possible without leaving the keyboard.
                  autoHighlight
                  // 56px tap targets per result, finger-friendly on tablet.
                  renderOption={(option: any) => (
                    <div className={classes.pickerOption}>{option.label}</div>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={pickerInputRef}
                      label="Add item or set menu to order"
                      placeholder="Type to search…"
                      variant="standard"
                      autoFocus
                    />
                  )}
                />
              </Paper>
            )}
          </Grid>

          {/* MIDDLE: suggestions — a dedicated column between the search
              and the preview, so matches never overlap the field/keyboard. */}
          {showTabletPicker && (
            <Grid item xs={12} md={4} className={classes.middleCol}>
              <div className={classes.tabletResults}>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((o) => (
                    <button
                      key={`${o.kind}-${o.id}`}
                      type="button"
                      className={classes.tabletResultRow}
                      onClick={() => {
                        if (o.kind === 'item') handlePickItem(o.menuItem);
                        else handlePickSetMenu(o.setMenu);
                      }}
                    >
                      <span>{o.label}</span>
                      <span className={classes.tabletResultKind}>
                        {o.kind === 'setMenu' ? 'Set menu' : 'Item'}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className={classes.tabletResultsEmpty}>
                    {pickerInput
                      ? `No items match “${pickerInput}”`
                      : 'Type to search the menu…'}
                  </div>
                )}
              </div>
            </Grid>
          )}

          {/* RIGHT: cart preview */}
          <Grid
            item
            xs={12}
            md={showTabletPicker ? 4 : 7}
            className={classes.rightCol}
          >
            <Typography variant="overline" color="textSecondary">
              Order
            </Typography>

            <div className={classes.cartScroll}>
              {units.length === 0 && (
                <div className={classes.emptyCart}>
                  No items yet. Pick one from the search to start.
                </div>
              )}

              {units.map((u) => {
                if (u.kind === 'setMenu') {
                  return (
                    <Paper
                      key={`sm-${u.groupId}`}
                      className={classes.setMenuCard}
                      elevation={1}
                    >
                      <div className={classes.setMenuHeader}>
                        <Typography
                          variant="body1"
                          className={classes.setMenuName}
                          noWrap
                          title={u.name}
                        >
                          {u.name}
                        </Typography>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="body1"
                            className={classes.setMenuPrice}
                          >
                            €{u.price.toFixed(2)}
                          </Typography>
                          <IconButton
                            size="small"
                            disabled={readonly}
                            className={classes.removeIcon}
                            onClick={(e) =>
                              onRemoveMenuItem(e, u.indices[0])
                            }
                          >
                            <RemoveIcon />
                          </IconButton>
                        </div>
                      </div>
                      <div className={classes.setMenuChoices}>
                        {u.items.map((it, k) => {
                          const mi = menuItems.find(
                            (m) => m.id === it.menuItemId,
                          );
                          return (
                            <Typography
                              key={k}
                              className={classes.setMenuChoice}
                              noWrap
                              title={mi?.title || ''}
                            >
                              • {mi?.title || '—'}
                            </Typography>
                          );
                        })}
                      </div>
                      <TextField
                        label="Note"
                        fullWidth
                        size="small"
                        className={classes.setMenuNote}
                        value={form.items?.[u.indices[0]]?.notes || ''}
                        disabled={readonly}
                        onChange={(e) =>
                          onChangeMenuItem(e, 'notes', u.indices[0])
                        }
                      />
                    </Paper>
                  );
                }

                const { item, index } = u;
                const mi = menuItems.find((m) => m.id === item.menuItemId);
                const lineTotal =
                  parseFloat(item.price?.toString() || '0') *
                  parseFloat(item.quantity?.toString() || '1');
                return (
                  <Paper
                    key={`it-${index}`}
                    className={classes.itemCard}
                    elevation={1}
                  >
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="body1"
                          className={classes.itemTitle}
                          noWrap
                          title={mi?.title || ''}
                        >
                          {mi?.title || '—'}
                        </Typography>
                      </Grid>

                      <Grid item xs={3} sm={2}>
                        <TextField
                          type="number"
                          label="Qty"
                          fullWidth
                          size="small"
                          value={item.quantity || 1}
                          disabled={readonly}
                          inputProps={{ min: 1, step: 1 }}
                          onChange={(e) =>
                            onChangeMenuItem(e, 'quantity', index)
                          }
                        />
                      </Grid>

                      <Grid item xs={5} sm={2}>
                        <Typography
                          variant="body2"
                          align="right"
                          style={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          €{lineTotal.toFixed(2)}
                        </Typography>
                      </Grid>

                      <Grid item xs={4} sm={2} style={{ textAlign: 'right' }}>
                        <IconButton
                          size="small"
                          disabled={readonly}
                          className={classes.removeIcon}
                          onClick={(e) => onRemoveMenuItem(e, index)}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Note"
                          fullWidth
                          size="small"
                          value={item.notes || ''}
                          disabled={readonly}
                          onChange={(e) =>
                            onChangeMenuItem(e, 'notes', index)
                          }
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}
            </div>

            <Typography
              variant="h6"
              align="right"
              className={classes.totalLine}
            >
              <strong>Total:</strong> €{totalAmount.toFixed(2)}
            </Typography>
          </Grid>
        </Grid>

        <ChooseSetMenuDialog
          addSetMenuItems={addSetMenuItems}
          initialSetMenuId={preselectedSetMenuId}
          dialogProps={{
            open: openSetMenu,
            onClose: () => {
              setOpenSetMenu(false);
              // Clear the preselect after a tick so the close animation
              // doesn't show the title flipping back to "Choose Set Menu".
              setTimeout(() => setPreselectedSetMenuId(undefined), 250);
            },
            fullWidth: true,
            maxWidth: 'sm',
          }}
        />
      </DialogContent>
      <DialogControls {...dialogControlProps} />
      <div hidden>
        <div ref={componentRef}>
          <PrintOrderReceipt orderId={orderId} />
        </div>
      </div>
    </React.Fragment>
  );

  function addSetMenuItems(items: SetMenuItemForm[], price: number) {
    const newMenuItems = form.items?.slice() ?? [];
    let counter = maxSetMenuItemGrouped + 1;
    setMaxSetMenuItemGrouped(counter);
    setForm((f) => ({
      ...f,
      setMenuPrice:
        parseFloat(f.setMenuPrice.toString()) + parseFloat(price.toString()),
      items: [
        ...newMenuItems,
        ...items.map((i) => ({
          menuItemId: i.menuItemId,
          quantity: 1,
          setMenuName: i.setMenuName ?? undefined,
          setMenuCategoryItemId: i.setMenuCategoryItemId,
          setMenuGroupId: counter,
          setMenuGroupPrice: price,
        })),
      ],
    }));
  }

  function onAddMenuItem(e: React.MouseEvent<HTMLElement>) {
    // Legacy "Add menu item" button is no longer rendered — items are
    // added by picking from the top autocomplete. Kept as a no-op so any
    // external caller (or a stale prop drilling) doesn't break.
  }

  // Pick handler for the top autocomplete: appends the chosen item to
  // the cart (form.items) with qty=1 and that item's price, clears the
  // search input, and refocuses it on the next tick so the soft keyboard
  // stays up and staff can immediately type the next item.
  function handlePickItem(mi: MenuItem | null) {
    if (!mi) return;
    const next = form.items?.slice() ?? [];
    next.push({
      menuItemId: mi.id,
      quantity: 1,
      price: mi.price,
    });
    setForm((f) => ({ ...f, items: next }));
    setPickerInput('');
    // setTimeout 0 so the refocus runs AFTER MUI's internal blur handling
    // for the picked option. Without this the keyboard collapses for one
    // frame on tablet.
    setTimeout(() => pickerInputRef.current?.focus(), 0);
  }

  // Handler when the user picks a set menu from the unified search.
  // Opens the choices dialog with the set menu preselected — the user
  // just ticks their picks and confirms. No refocus here: the dialog
  // takes over input and the keyboard is the wrong tool for ticking
  // checkboxes anyway.
  function handlePickSetMenu(sm: SetMenu) {
    setPreselectedSetMenuId(sm.id);
    setOpenSetMenu(true);
    setPickerInput('');
  }

  function printOrder(e: React.MouseEvent<HTMLElement>) {
    if (orderId !== undefined && handlePrint) {
      handlePrint();
    }
  }

  function isMenuItem(
    input:
      | React.ChangeEvent<
          HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement
        >
      | MenuItem,
  ): input is MenuItem {
    return input.hasOwnProperty('title');
  }

  function onChangeMenuItem(
    e:
      | React.ChangeEvent<
          HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement
        >
      | (MenuItem | null),
    name: keyof OrderItemsForm,
    i: number,
  ) {
    const value = e ? (isMenuItem(e) ? e.id : e.currentTarget.value) : null;

    const newMenuItems = form.items.slice();
    const data = newMenuItems[i];

    switch (name) {
      case 'notes':
        if (typeof value === 'string') {
          data[name] = value;
        }
        break;
      case 'menuItemId':
        // No longer fired by the redesigned UI (the top picker handles
        // adding new items directly via handlePickItem). Kept for
        // historical safety / read-only views that may still call it.
        if (typeof value === 'number') {
          const item = menuItems.find((i) => i.id === value);
          if (item) {
            data.price = item.price;
          }
        }
        data[name] = value ? parseFloat(value.toString()) : undefined;
        break;
      case 'quantity':
        if (typeof value === 'string') {
          data[name] = value ? parseFloat(value) : undefined;
        }
        break;
      default:
        return;
    }

    setForm((f) => ({
      ...f,
      items: newMenuItems,
    }));
  }

  function onRemoveMenuItem(e: React.MouseEvent<HTMLElement>, i: number) {
    if (!form.items) {
      return null;
    }

    const currentItem = form.items[i];
    let newMenuItems = form.items.slice();
    newMenuItems.splice(i, 1);

    let setMenuPrice = form.setMenuPrice;
    if (currentItem.setMenuCategoryItemId) {
      newMenuItems = newMenuItems.filter(
        (x) =>
          x.setMenuGroupId !== currentItem.setMenuGroupId ||
          !x.setMenuCategoryItemId,
      );
      setMenuPrice -= currentItem.setMenuGroupPrice ?? 0;
    }
    setForm((f) => ({
      ...f,
      items: newMenuItems,
      setMenuPrice:
        newMenuItems.filter((item) => item.setMenuCategoryItemId).length === 0
          ? 0
          : setMenuPrice,
    }));
  }
};
